import * as Yup from "yup";
import i18next from "../../i18n";
import { CompanyContactType } from "../../types";

/**
 * One contact row managed by `ContactsInformationForm`'s `useFieldArray`. Mirrors
 * `CompanyContactResponse` (`types/company.type.ts`) field-for-field — `id` stays optional/absent
 * on a freshly-added row, same convention `AddressFormFields` (Task 8) uses.
 */
export interface ContactFormFields {
  id?: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  type: CompanyContactType | "";
}

export interface ContactsFormFields {
  contacts: ContactFormFields[];
}

const maxLengthMessage = (max: number) =>
  i18next.t("validations.maxLength", {
    max,
    defaultValue: `Must not exceed ${max} characters`,
  });

/**
 * `name`/`email`/`phone`/`position`/`type` are ALL `.required()` — reproducing the pre-Carbon
 * "Add Contact" button's own gate EXACTLY (its `onClick`: `newContact?.name && newContact?.email
 * && newContact?.phone && newContact?.type && newContact?.position`, all truthy before a row
 * could ever be staged). That gate is also why requiring all five for every row — not just new
 * ones — is safe: the only path that ever created a `CompanyContact` row was through this exact
 * gate, so every already-persisted contact already satisfies it.
 *
 * `email` additionally carries `.email()` — the backend's own
 * `CompanyProfileUpdateRequest.ContactRequest.email` is annotated `@Email` (this task's brief
 * calls this out explicitly). The pre-Carbon form had no format check at all (a plain
 * `type="text"` input accepting any string), so this is a genuine new client-side gate — but not
 * an unsafe one: a malformed email now surfaces as a form error instead of a network 400 the
 * `@Valid`-cascaded `@Email` would produce anyway once this DTO's bean validation actually runs
 * (see that class's own top-of-file comment) — strictly better, not a new restriction on what can
 * be saved.
 *
 * Max-lengths mirror `ContactRequest`'s own `@Size` constraints exactly: `name`/`email`/
 * `position` 255, `phone` 50. `type` has no length constraint on the wire (it's a Java enum) —
 * `.required()` only.
 */
export const contactRowValidationSchema = Yup.object({
  id: Yup.string().optional(),
  name: Yup.string()
    .required(i18next.t("validations.contactName"))
    .max(255, maxLengthMessage(255)),
  email: Yup.string()
    .required(i18next.t("validations.email"))
    .email(i18next.t("validations.validEmail"))
    .max(255, maxLengthMessage(255)),
  phone: Yup.string()
    .required(i18next.t("validations.phone"))
    .max(50, maxLengthMessage(50)),
  position: Yup.string()
    .required(i18next.t("validations.contactPosition"))
    .max(255, maxLengthMessage(255)),
  type: Yup.string().required(i18next.t("validations.contactType")),
});

/**
 * The pre-Carbon form never validated the LIST itself (zero contacts was, and stays, a valid
 * state) — only each staged row, at the moment it was added. No `.min()`/`.required()` on the
 * array, same rationale as every other repeatable sub-form's schema (Tasks 8-9).
 */
export const contactsValidationSchema = Yup.object({
  contacts: Yup.array().of(contactRowValidationSchema).default([]),
});
